import { Link, useLocation } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useApi } from "@/src/lib/hooks/api/use-api";
import Icon from "../custom/Icon";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Button } from "../ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "../ui/dropdown-menu";

export default function UserDropdown() {
	const { t } = useTranslation();
	const { pathname } = useLocation();
	const { auth } = useApi();

	const { data: session } = auth.useSession();

	const isActive = (path: string) => pathname === path;

	if (!session?.user) {
		return (
			<Button variant="primary" asChild>
				<Link to="/auth/signin">
					<Icon name="LogIn" className="w-4 h-4" />
					{t("common.sign_in")}
				</Link>
			</Button>
		);
	}

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button
					variant="ghost"
					className="flex items-center gap-3 bg-accent rounded-full py-6"
				>
					<Avatar className="size-8">
						<AvatarImage
							src={session.user.image || undefined}
							alt={t("common.user_image_alt")}
						/>
						<AvatarFallback className="bg-primary/10 text-primary">
							{session.user.name
								? session.user.name.charAt(0).toUpperCase()
								: "U"}
						</AvatarFallback>
					</Avatar>
					<div className="hidden sm:block text-start">
						<div className="text-sm font-medium">{session.user.name}</div>
						<div className="text-xs text-muted-foreground">
							@{session.user.email.split("@")[0]}
						</div>
					</div>
					<Icon name="ChevronDown" className="size-4 text-primary" />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="w-56">
				<DropdownMenuLabel>{t("common.my_account")}</DropdownMenuLabel>
				<DropdownMenuSeparator />
				<DropdownMenuItem asChild>
					<Link
						to="/profile"
						className={`flex items-center ${isActive("/profile") ? "bg-primary/10 text-primary" : ""}`}
					>
						<Icon name="User" className="w-4 h-4 mr-3" />
						{t("nav.profile")}
					</Link>
				</DropdownMenuItem>
				<DropdownMenuItem asChild>
					<Link
						to="/settings"
						className={`flex items-center ${isActive("/settings") ? "bg-primary/10 text-primary" : ""}`}
					>
						<Icon name="Settings" className="w-4 h-4 mr-3" />
						{t("nav.settings")}
					</Link>
				</DropdownMenuItem>
				<DropdownMenuItem asChild>
					<Link
						to="/support"
						className={`flex items-center ${isActive("/support") ? "bg-primary/10 text-primary" : ""}`}
					>
						<Icon name="Info" className="w-4 h-4 mr-3" />
						{t("nav.support")}
					</Link>
				</DropdownMenuItem>
				<DropdownMenuSeparator />
				<DropdownMenuItem
					className="text-destructive focus:text-destructive"
					onClick={() => auth.signOut()}
				>
					<Icon name="LogOut" className="w-4 h-4 mr-3" />
					{t("common.sign_out")}
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
